import React, { useState } from 'react'
import { reactular } from '@vendhq/reactular'
import { Card, Scrollable } from '@lightspeed/design-system-react'
import { ApiCustomer, ApiRetailerPaymentType } from '@vend/types'
import classNames from 'classnames'
import { vn, round } from 'vend-number'
import { useSaleManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleLocalId,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useFormatCurrencyValue } from '../summary-panel/useFormatCurrencyValue'
import { PayLinkLimitModal } from '../../payments/pay-link-limit-modal/PayLinkLimitModal'
import { CashDiscountingBanner } from './CashDiscountingBanner'
import { PaymentOptions } from './PaymentOptions'
import { useFetchPaymentTypes } from './hooks/useFetchPaymentTypes'
import { LSPayButton } from './LSPayButton'
import { AmountTendered } from './AmountTendered/AmountTendered'
import { usePaymentInit } from './hooks/usePaymentInit'
import { CustomerSection } from './CustomerSection'
import { useShowSaveSaleButton } from './hooks/useShowSaveSaleButton'
import { SaveSale } from './SaveSale'
import { CustomerPaymentOptions } from './CustomerPaymentOptions'
import { CustomerPaymentTypeMessage } from './CustomerPaymentTypeMessage/CustomerPaymentTypeMessage'
import { usePreventPageReload } from './hooks/usePreventPageReload'
import { useHandlePaymentType } from './hooks/useHandlePayment'
import { PaymentModal } from './PaymentModal/PaymentModal'
import { useLaybyOnAccountSale } from './hooks/useLaybyOnAccountSale'
import {
  RecoveryPaymentOptions,
  useTransactionRecovery,
} from './hooks/useTransactionRecovery'
import { useLSPayQuickPay } from './hooks/useLSPayQuickPay'
import { PayPalRefundButton } from './PayPalRefundButton'
import { useEcomPaymentTypes } from './hooks/useEcomPaymentTypes'
import { LSPayFlow } from './PaymentModal/types'
import { PaymentRequestButton } from './PaymentRequest/PaymentRequestButton'

export type PaymentData = {
  recoveryPaymentOptions?: RecoveryPaymentOptions
  // Optional override to route specific LSPay sub-flows while keeping the same paymentType.
  lspayFlow?: LSPayFlow
  // For PMOF: the selected saved payment method id to charge
  paymentMethodOnFileId?: string
}

export const PaymentPanel = () => {
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const saleManager = useSaleManager()

  const formatCurrencyValue = useFormatCurrencyValue()

  const cashDiscountingOutstandingBalance = saleManager.getCashDiscountedOutstandingBalance()
  const {
    filteredPaymentTypes,
    systemPaymentTypes,
    fetchPaymentTypes,
  } = useFetchPaymentTypes()
  const eComPaymentTypes = useEcomPaymentTypes()
  const { allowPageReload, preventPageReload } = usePreventPageReload()
  const localId = useAppSelector(selectSaleLocalId)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const [amountTendered, setAmountTendered] = useState(
    round(outstandingBalance.toString(), 2)
  )

  const [showSaveSaleButton] = useShowSaveSaleButton()

  /**
   * need to update amount tendered when:
   *  - customer removed, customer changed, or when account balance payment added but not paid
   * set amountTendered to certain value if provided
   * otherwise set it to the sale outstanding balance
   */
  const updateAmountTendered = (newAmount?: number | string) => {
    if (newAmount !== undefined && newAmount !== null) {
      const amountStr = newAmount.toString()
      if (amountStr.trim() === '') {
        setAmountTendered('')
      } else if (!isNaN(Number(amountStr))) {
        setAmountTendered(round(amountStr, 2))
      } else {
        setAmountTendered(amountStr) // For non-numeric input, let validation handle
      }
    } else {
      setAmountTendered(round(outstandingBalance.toString(), 2))
    }
  }

  // register & deregister init events once when component mount / unmount
  usePaymentInit({ fetchPaymentTypes, updateAmountTendered })

  const [hasPaymentAmountError, setHasPaymentAmountError] = useState(false)

  const {
    handlePayment,
    handleCompletePayment,
    selectedPaymentType,
    clearSelectedPaymentType,
  } = useHandlePaymentType(
    amountTendered,
    paymentProcessing,
    hasPaymentAmountError,
    setPaymentProcessing,
    allowPageReload,
    updateAmountTendered
  )

  const pay = (
    paymentType: ApiRetailerPaymentType,
    paymentData?: PaymentData
  ) => {
    preventPageReload()
    handlePayment(paymentType, paymentData)
  }

  //  If we have a payment to recover, we need to handle that first
  useTransactionRecovery({
    lspayPaymentType: systemPaymentTypes.lspayPaymentType,
    pay,
  })

  //  Handle the case for lspay quick pay
  useLSPayQuickPay({
    lspayPaymentType: systemPaymentTypes.lspayPaymentType,
    pay,
  })

  const handleCustomerSelect = (customer?: ApiCustomer) => {
    if (!customer) {
      return
    }
    saleManager.addCustomer(customer).then(() => {
      updateAmountTendered()
    })
  }

  const {
    laybyOnAccountSale,
    isPayLinkModalOpen,
    handlePayLinkLimitModalClose,
  } = useLaybyOnAccountSale(
    localId,
    amountTendered,
    paymentProcessing,
    setPaymentProcessing
  )

  return (
    <>
      <Card className="payment-panel--action">
        <Scrollable monobrow={true} goatee={true}>
          <div
            className={classNames(
              'vd-flex vd-flex--column payment-panel-content--action',
              {
                'pro-payments-ready': !paymentProcessing,
              }
            )}
          >
            {saleManager.hasCashDiscountingServiceFee() &&
            cashDiscountingOutstandingBalance ? (
              <CashDiscountingBanner
                balance={formatCurrencyValue(cashDiscountingOutstandingBalance)}
              />
            ) : null}
            <AmountTendered
              amountTendered={amountTendered}
              onChange={setAmountTendered}
              setHasError={setHasPaymentAmountError}
            />
            <PayPalRefundButton
              paymentType={eComPaymentTypes.paypalEcomPaymentType}
              pay={pay}
            />
            <LSPayButton
              paymentType={systemPaymentTypes.lspayPaymentType}
              pay={pay}
              disabled={hasPaymentAmountError}
            />
            <PaymentOptions
              pay={pay}
              amountTendered={amountTendered}
              hasValidTenderedAmount={!hasPaymentAmountError}
              filteredPaymentTypes={filteredPaymentTypes}
              systemPaymentTypes={systemPaymentTypes}
            />
            <CustomerSection
              onSelect={handleCustomerSelect}
              canAddAccountBalancePayment={true}
            />
            <PaymentRequestButton />
            <CustomerPaymentOptions
              systemPaymentTypes={systemPaymentTypes}
              pay={pay}
              laybyOnAccountSale={laybyOnAccountSale}
              amountTendered={amountTendered}
              hasPaymentAmountError={hasPaymentAmountError}
            />
            <CustomerPaymentTypeMessage amountTendered={amountTendered} />
          </div>
        </Scrollable>
        {showSaveSaleButton && (
          <div className="sale-action-bar save-sale">
            <SaveSale />
          </div>
        )}
      </Card>
      {selectedPaymentType && (
        <PaymentModal
          tenderAmount={amountTendered}
          paymentOptions={selectedPaymentType.paymentOptions}
          onClose={clearSelectedPaymentType}
          onSuccess={handleCompletePayment}
          updateAmountTendered={updateAmountTendered}
        />
      )}
      {isPayLinkModalOpen && (
        <PayLinkLimitModal
          amount={vn(amountTendered).toNumber()}
          close={handlePayLinkLimitModalClose}
        />
      )}
    </>
  )
}

export const ReactPaymentPanel = reactular(PaymentPanel, [], 'reactWrapper')
